import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  EnvelopeIcon,
  LockClosedIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../../contexts/ToastContext';
import { sendOtpReset, resetPassword } from '../../api/authApi';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestOtp = async (e) => {
  e.preventDefault();
  setIsLoading(true);

  try {
    await sendOtpReset(email.trim());
    showToast("Verification Code Sent", "success");
    setStep(2);
  } catch (err) {
    showToast(
      err.response?.data?.message || "Error sending code",
      "error"
    );
  } finally {
    setIsLoading(false);
  }
};

  const handleResetPassword = async (e) => {
  e.preventDefault();
  setIsLoading(true);

  try {
    await resetPassword({
      email: email.trim(),
      otp_code: otp.trim(),
      new_password: newPassword
    });

    showToast("Password Updated Successfully", "success");
    navigate("/login");
  } catch (err) {
    showToast(
      err.response?.data?.message || "Invalid or Expired Code",
      "error"
    );
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-4">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        className="max-w-md w-full bg-[#0a0a0a] border border-white/5 rounded-[2rem] p-8 md:p-10 shadow-2xl"
      >
        <Link to="/login" className="inline-flex items-center text-gray-600 hover:text-indigo-400 text-[10px] font-black uppercase italic mb-6 transition-colors">
          <ArrowLeftIcon className="h-3 w-3 mr-2" /> Back
        </Link>

        <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter mb-8">
          {step === 1 ? "Recovery" : "New Password"}
        </h2>

        <form onSubmit={step === 1 ? handleRequestOtp : handleResetPassword} className="space-y-4">
          {step === 1 ? (
            <div className="relative group">
              <EnvelopeIcon className="absolute left-4 top-4 h-5 w-5 text-gray-600 group-focus-within:text-indigo-500 transition-colors" />
              <input 
                type="email" 
                placeholder="Email Address" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-indigo-500/50 transition-all text-sm italic" 
                required 
              />
            </div>
          ) : (
            <>
              <div className="relative group">
                <KeyIcon className="absolute left-4 top-4 h-5 w-5 text-gray-600 group-focus-within:text-indigo-500 transition-colors" />
                <input 
                  type="text" 
                  maxLength="6"
                  placeholder="6-Digit Code" 
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-indigo-500/50 transition-all text-sm font-mono tracking-[0.3em]" 
                  required 
                />
              </div>

              <div className="relative group">
                <LockClosedIcon className="absolute left-4 top-4 h-5 w-5 text-gray-600 group-focus-within:text-indigo-500 transition-colors" />
                <input 
                  type={showPassword ? "text" : "password"} 
                  placeholder="New Password" 
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-indigo-500/50 transition-all text-sm italic" 
                  required 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-4 text-gray-600"
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </>
          )}

          <button 
            disabled={isLoading} 
            className="w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] text-white bg-indigo-600 hover:bg-indigo-500 flex items-center justify-center transition-all active:scale-95 italic disabled:opacity-50 mt-4"
          >
            {isLoading ? "Processing" : step === 1 ? "Send Code" : "Update"}
            {!isLoading && <ArrowRightIcon className="h-4 w-4 ml-2" />}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;